import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, Download, FileText, Flame, HelpCircle, Minus, Search, Send, Trash2, Upload, X, Zap } from "lucide-react";
import { Badge } from "@/components/Badge";
import { Stepper } from "@/components/Stepper";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState } from "@/components/EmptyState";
import { ReadinessPanel } from "@/components/ReadinessPanel";
import { StatoInvioBanner } from "@/components/StatoInvioBanner";
import { SchedaSpesaVenditaPratica } from "@/components/analisi/SchedaSpesaVenditaPratica";
import { InvioA2ADaPratica } from "./InvioA2ADaPratica";
import { STAGE_COLORS, tipoPraticaLabel, isBusiness } from "@/lib/constants";
import { getPraticaAgenzia, deletePraticaAgenzia, generaModuliPratica, uploadModuliFirmati, deleteModuloFirmato, setTipoModuloFirmato, urlDownloadModuloFirmato, resendPrivacyLink, getPrivacyStatus } from "@/api/agenzia";
import type { ModuloGenerato, PrivacyStatus } from "@/api/agenzia";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { downloadBollettaFile } from "@/api/bollette";
import { ApiError } from "@/api/client";
import { formatDate, formatDateTime } from "@/lib/dates";
import type { DettaglioPraticaAgenzia, ModuloTipo } from "@/types";
import { LABEL_MODULO } from "@/types";
import { OnboardingTour } from "@/onboarding/OnboardingTour";
import { dettaglioPraticaSteps, dettaglioPraticaVersion } from "@/onboarding/dettaglioPraticaSteps";
import { useAuth } from "@/auth/useAuth";

async function downloadDocumento(contactId: string, label: string): Promise<void> {
  const res = await fetch(`/api/espo/agenzia/clienti/${encodeURIComponent(contactId)}/documento`, {
    credentials: "include",
  });
  if (res.status === 404) throw new Error("Documento non disponibile");
  if (res.status === 403) throw new Error("Documento non abilitato per la tua agenzia");
  if (res.status === 502) throw new Error("Impossibile verificare l'abilitazione, riprova");
  if (!res.ok) throw new Error("Errore nel download del documento");

  const cd = res.headers.get("Content-Disposition");
  const cdMatch = cd?.match(/filename="?([^"]+)"?/);
  const filename = cdMatch?.[1] || `documento_${label}`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span>{" "}
      <span className="text-text">{value ?? "—"}</span>
    </div>
  );
}

// Riga stato consenso: icona + testo (mai solo colore).
// state: true = sì, false = no, null = mai risposto, undefined = in caricamento.
function PrivacyStatusRow({
  label, state, yes, no,
}: { label: string; state: boolean | null | undefined; yes: string; no: string }) {
  return (
    <div className="flex items-center gap-2">
      {state === true && <Check size={14} className="text-green-700 shrink-0" />}
      {state === false && <X size={14} className="text-red-600 shrink-0" />}
      {(state === null || state === undefined) && (
        <Minus size={14} className="text-muted-foreground shrink-0" />
      )}
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-text">
        {state === undefined ? "…" : state === null ? "mai risposto" : state ? yes : no}
      </span>
    </div>
  );
}

export function DettaglioPraticaAgenziaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DettaglioPraticaAgenzia | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [replayNonce, setReplayNonce] = useState(0);
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");
  const canDelete =
    (hasRole("affiliato") || hasRole("operatore_supervisore")) &&
    data?.stage === "Bozza";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);
  const [moduliGenerati, setModuliGenerati] = useState<ModuloGenerato[]>([]);
  const [moduliError, setModuliError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [statoInvio, setStatoInvio] = useState<string | null>(null);
  const [confirmFirmato, setConfirmFirmato] = useState<{ id: string; name: string } | null>(null);
  const [deletingFirmato, setDeletingFirmato] = useState(false);
  const [sendingPrivacyLink, setSendingPrivacyLink] = useState(false);
  const [privacyLinkError, setPrivacyLinkError] = useState<string | null>(null);
  const [privacyLinkSent, setPrivacyLinkSent] = useState(false);
  const [privacyStatus, setPrivacyStatus] = useState<PrivacyStatus | null>(null);

  useEffect(() => {
    if (!data?.cliente_id) return;
    let cancelled = false;
    getPrivacyStatus(data.cliente_id)
      .then((s) => { if (!cancelled) setPrivacyStatus(s); })
      .catch(() => { /* sezione stato opzionale: in errore resta il placeholder */ });
    return () => { cancelled = true; };
  }, [data?.cliente_id]);

  async function handleDelete() {
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePraticaAgenzia(id);
      navigate("/agenzia/pratiche");
    } catch (e) {
      setDeleteError(
        e instanceof ApiError && e.detail ? e.detail : "Impossibile cancellare la pratica.",
      );
      setDeleting(false);
    }
  }

  async function handleResendPrivacyLink() {
    if (!data?.cliente_id) return;
    setSendingPrivacyLink(true);
    setPrivacyLinkError(null);
    setPrivacyLinkSent(false);
    try {
      await resendPrivacyLink(data.cliente_id);
      setPrivacyLinkSent(true);
    } catch (e) {
      setPrivacyLinkError(
        e instanceof ApiError && e.detail ? e.detail : "Impossibile inviare il link privacy.",
      );
    } finally {
      setSendingPrivacyLink(false);
    }
  }

  // Il pannello moduli si mostra dove il backend dichiara moduli richiesti (moduli_stato),
  // non da una lista di tipi ricopiata qui: la lista viveva già in constants.py /
  // moduli_stato.py / forms.generate.py e la copia client si era disallineata — "Nuova
  // attivazione" era gated dal gate A2A ma senza UI per caricare i firmati (vicolo cieco,
  // segnalazione Giangrossi 2026-08-03).
  const richiedeModuli = (data?.moduli_stato?.richiesti.length ?? 0) > 0;

  async function handleGenera() {
    if (!id) return;
    setGenerando(true);
    setModuliError(null);
    try {
      const res = await generaModuliPratica(id);
      // NON auto-aprire i link: il popup-blocker consente solo la prima apertura _blank per
      // gesto utente → ne scaricava uno solo. Mostriamo TUTTI i moduli come lista di link.
      setModuliGenerati(res.moduli);
    } catch (e) {
      setModuliError(e instanceof ApiError ? (e.detail || e.message) : "Errore nella generazione");
    } finally {
      setGenerando(false);
    }
  }

  async function handleUpload(ev: React.ChangeEvent<HTMLInputElement>) {
    if (!id || !ev.target.files?.length) return;
    const files = Array.from(ev.target.files);
    ev.target.value = "";
    setUploading(true);
    setModuliError(null);
    setStatoInvio(null);
    try {
      const res = await uploadModuliFirmati(id, files);
      setStatoInvio(res.stato_invio || null);
      setData(await getPraticaAgenzia(id));   // ricarica: moduli_stato + tipi auto-rilevati + readiness
    } catch (e) {
      setModuliError(e instanceof ApiError ? (e.detail || e.message) : "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteFirmato(attId: string) {
    if (!id) return;
    setModuliError(null);
    try {
      await deleteModuloFirmato(id, attId);
      setData(await getPraticaAgenzia(id));   // ricarica: moduli_stato ricalcolato dal backend
    } catch (e) {
      setModuliError(e instanceof ApiError ? (e.detail || e.message) : "Errore nell'eliminazione");
    }
  }

  async function handleSetTipo(attId: string, tipo: ModuloTipo) {
    if (!id) return;
    setModuliError(null);
    try {
      await setTipoModuloFirmato(id, attId, tipo);
      setData(await getPraticaAgenzia(id));   // single source: stato + tipi dal backend
    } catch (e) {
      setModuliError(e instanceof ApiError ? (e.detail || e.message) : "Errore nell'assegnazione del tipo");
    }
  }

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getPraticaAgenzia(id)
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) {
          setNotFound(true);
        } else {
          setError("Impossibile caricare la pratica.");
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  // I passi su offerta / bollette / documento / moduli / invio si mostrano da sé
  // solo dove la card esiste: OnboardingTour scarta i passi il cui elemento non
  // è in pagina.

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;
  }
  if (notFound) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/agenzia/pratiche")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
        >
          <ArrowLeft size={16} /> Torna alle pratiche
        </button>
        <EmptyState icon={<Search size={26} className="text-brand" />} title="Pratica non trovata" />
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const stageColor = STAGE_COLORS[data.stage] ?? STAGE_COLORS["Bozza"];

  return (
    <div className="space-y-4">
      <OnboardingTour
        steps={dettaglioPraticaSteps}
        flagKey="dettaglio_pratica"
        version={dettaglioPraticaVersion}
        replayNonce={replayNonce}
      />
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/agenzia/pratiche")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-text"
        >
          <ArrowLeft size={16} /> Torna alle pratiche
        </button>
        {isStaff && (
          <button
            type="button"
            onClick={() => setReplayNonce((n) => n + 1)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text transition-colors"
            title="Rivedi guida"
          >
            <HelpCircle size={14} />
            <span>Rivedi guida</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-text">{data.name}</h2>
        <Badge bg={stageColor.bg} text={stageColor.text}>{data.stage}</Badge>
        {canDelete && (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            data-tour="pratica-cancella"
            className="ml-auto flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700"
            title="Cancella pratica"
          >
            <Trash2 size={16} /> Cancella
          </button>
        )}
      </div>

      <div data-tour="pratica-stepper">
        <Stepper currentStage={data.stage} />
      </div>

      <div data-tour="pratica-invio">
        <ReadinessPanel
          readiness={data.readiness}
          autoAttivo={data.invio_automatico_attivo ?? false}
          utenza={data.utenza_fornitore}
          utenzaNonDisponibile={data.utenza_fornitore_non_disponibile}
        />
      </div>

      {/* Le utenze custodite partono col cron: il bottone serve solo a chi lavora a
          sessione temporanea, dove l'invio parte quando l'agenzia è dentro. */}
      {data.utenza_fornitore?.modalita === "sessione_temporanea" && data.readiness?.pronta && (
        <div data-tour="pratica-invia-a-mano">
        <InvioA2ADaPratica
          oppId={data.id}
          utenza={data.utenza_fornitore}
          onInviata={() => { getPraticaAgenzia(data.id).then(setData).catch(() => {}); }}
        />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div data-tour="pratica-dati" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Dati pratica</h3>
          <div className="space-y-2 text-sm">
            <Field label="Tipo" value={data.tipo} />
            <Field label="Importo" value={data.amount != null ? `€${data.amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : null} />
            <Field label="Data creazione" value={formatDateTime(data.created_at)} />
            <Field label="Data chiusura" value={formatDate(data.close_date)} />
            <Field label="Agenzia" value={data.account_name} />
            <Field label="Commissione" value={data.quota_agenzia != null ? `€${data.quota_agenzia.toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : null} />
            {/* Il numero con cui A2A conosce la pratica: si detta all'assistenza. Compare
                solo quando c'è (pratica inviata e già vista da un giro di polling). */}
            {data.a2a_numero_pratica && (
              <div className="break-words">
                <span className="text-muted-foreground">Codice A2A:</span>{" "}
                <span className="text-text font-mono">{data.a2a_numero_pratica}</span>
              </div>
            )}
          </div>
        </div>

        <div data-tour="pratica-cliente" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Cliente</h3>
          <div className="space-y-2 text-sm">
            <div className="break-words">
              <span className="text-muted-foreground">Nome:</span>{" "}
              {data.cliente_id ? (
                <Link
                  to={`/agenzia/clienti/${data.cliente_id}`}
                  className="text-brand hover:underline"
                >
                  {data.cliente_name ?? "—"}
                </Link>
              ) : (
                <span className="text-text">{data.cliente_name ?? "—"}</span>
              )}
              {isBusiness(data.tipo_soggetto) && (
                <Badge bg="bg-indigo-100" text="text-indigo-800" className="ml-2">Business</Badge>
              )}
            </div>
            <Field label="CF / P.IVA" value={data.codice_fiscale} />
            <Field label="IBAN" value={data.iban} />
          </div>
          {data.cliente_id && (
            <div data-tour="pratica-privacy" className="mt-3 pt-3 border-t border-border">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Privacy e consensi
              </p>
              <div className="space-y-1.5 text-sm mb-3">
                <div className="flex items-center gap-2">
                  {privacyStatus === null ? (
                    <>
                      <Minus size={14} className="text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Informativa:</span>
                      <span className="text-text">…</span>
                    </>
                  ) : privacyStatus.presa_visione ? (
                    <>
                      <Check size={14} className="text-green-700 shrink-0" />
                      <span className="text-muted-foreground">Informativa:</span>
                      <span className="text-text">confermata dal cliente</span>
                    </>
                  ) : privacyStatus.informativa_consegnata ? (
                    <>
                      <Check size={14} className="text-green-700 shrink-0" />
                      <span className="text-muted-foreground">Informativa:</span>
                      <span className="text-text">consegnata</span>
                    </>
                  ) : (
                    <>
                      <Minus size={14} className="text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Informativa:</span>
                      <span className="text-text">non risulta — reinvia il link</span>
                    </>
                  )}
                </div>
                <PrivacyStatusRow
                  label="Email / WhatsApp"
                  state={privacyStatus ? privacyStatus.marketing : undefined}
                  yes="consenso dato"
                  no="consenso negato"
                />
                <PrivacyStatusRow
                  label="Telefono"
                  state={privacyStatus ? privacyStatus.marketing_telefono : undefined}
                  yes="consenso dato"
                  no="consenso negato"
                />
              </div>
              {privacyLinkError && (
                <p className="text-sm text-red-600 mb-2">{privacyLinkError}</p>
              )}
              {privacyLinkSent && !privacyLinkError && (
                <p className="text-sm text-green-700 mb-2">Link privacy inviato al cliente.</p>
              )}
              <button
                type="button"
                onClick={handleResendPrivacyLink}
                disabled={sendingPrivacyLink}
                className="flex items-center gap-2 text-sm text-brand hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={14} />
                {sendingPrivacyLink ? "Invio…" : "Reinvia link privacy"}
              </button>
            </div>
          )}
        </div>
      </div>

      {data.offerta_scelta && (
        <div data-tour="pratica-offerta" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Offerta scelta</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Field label="Offerta" value={data.offerta_scelta.nome} />
            <Field label="Fornitore" value={data.offerta_scelta.fornitore} />
            <Field label="Tipo" value={data.offerta_scelta.tipo} />
            <Field label="Prezzo" value={data.offerta_scelta.prezzo} />
            <Field label="Quota fissa" value={`€${data.offerta_scelta.quota_fissa_mese.toFixed(2)}/mese`} />
            <Field label="Durata" value={`${data.offerta_scelta.durata_mesi} mesi`} />
            <Field label="Fatturazione" value={data.offerta_scelta.fatturazione} />
          </div>
        </div>
      )}

      {data.bollette && data.bollette.length > 0 && (
        <div data-tour="pratica-bollette" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-1">Bollette collegate</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Le bollette da cui è nata questa pratica
          </p>
          <div className="space-y-2">
            {data.bollette.map((b) => (
              <div
                key={b.id}
                className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10">
                  {b.tipoFornitura === "Gas"
                    ? <Flame className="h-4 w-4 text-orange-500" />
                    : <Zap className="h-4 w-4 text-amber-500" />}
                </span>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/agenzia/bollette/${b.id}`}
                    className="block truncate font-medium text-text hover:text-brand hover:underline"
                    title={b.name}
                  >
                    {b.name}
                  </Link>
                  <div className="text-xs text-muted-foreground truncate">
                    {[
                      b.tipoFornitura,
                      b.fornitoreAttuale,
                      b.dataBolletta ? formatDate(b.dataBolletta) : null,
                      b.importoMensile ? `€ ${b.importoMensile.toFixed(2)}/mese` : null,
                    ].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                {b.hasFile && (
                  <button
                    onClick={() => downloadBollettaFile(b.id, `${b.name}.pdf`, true).catch(() => {})}
                    className="flex items-center gap-1 text-xs text-brand hover:underline flex-shrink-0"
                  >
                    <Download size={14} /> Scarica
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <SchedaSpesaVenditaPratica oppId={data.id} />

      {data.has_documento && data.cliente_id && (
        <div data-tour="pratica-documento" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Documento identità</h3>
          {docError && (
            <p className="text-sm text-red-600 mb-2">{docError}</p>
          )}
          <button
            onClick={() => {
              setDocError(null);
              downloadDocumento(data.cliente_id!, data.cliente_name ?? data.cliente_id!)
                .catch((e) => setDocError(e instanceof Error ? e.message : "Errore download"));
            }}
            className="flex items-center gap-2 text-sm text-brand hover:underline"
          >
            <Download size={14} /> Scarica documento
          </button>
        </div>
      )}

      {richiedeModuli && (
        <div data-tour="pratica-moduli" className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-1">Moduli {tipoPraticaLabel(data.tipo_pratica).toLowerCase()}</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Genera i moduli precompilati, falli firmare al cliente, poi ricarica qui le copie firmate (PDF).
          </p>
          {moduliError && <p className="mb-3 text-sm text-red-600">{moduliError}</p>}

          <StatoInvioBanner statoInvio={statoInvio} />

          {data.moduli_stato && !data.moduli_stato.completo && (
            <p className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>
                Mancano i moduli firmati: {data.moduli_stato.mancanti.map((t) => LABEL_MODULO[t]).join(", ")}.
                La pratica non può essere inviata ad A2A finché non sono caricati e assegnati.
              </span>
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 mb-5">
            <button
              onClick={handleGenera}
              disabled={generando}
              className="flex items-center justify-center gap-2 bg-brand text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Download size={16} />
              {generando ? "Generazione…" : "Genera moduli"}
            </button>
            <label
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border text-text hover:bg-brand/5 cursor-pointer transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
            >
              <Upload size={16} />
              {uploading ? "Caricamento…" : "Carica firmati"}
              <input
                type="file"
                accept="application/pdf"
                multiple
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
          </div>

          {moduliGenerati.length > 0 && (
            <div className="mb-5 rounded-lg border border-border bg-brand/5 px-3 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Moduli generati ({moduliGenerati.length}) — scaricali, falli firmare, poi ricaricali qui sotto
              </p>
              <div className="space-y-1.5">
                {moduliGenerati.map((m) => (
                  <a
                    key={m.filename}
                    href={m.url}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-2 text-sm text-brand hover:underline"
                  >
                    <Download size={14} /> {m.filename}
                  </a>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Moduli firmati
          </p>
          {!data.moduli_firmati?.length ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
              <FileText className="h-6 w-6 mx-auto text-muted-foreground mb-1.5" />
              <p className="text-sm text-muted-foreground">Nessun modulo firmato caricato.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.moduli_firmati.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
                >
                  <FileText className="h-4 w-4 text-brand flex-shrink-0" />
                  <a
                    href={urlDownloadModuloFirmato(data.id, m.id)}
                    className="flex-1 min-w-0 truncate text-sm font-medium text-text hover:text-brand hover:underline"
                    title={m.name}
                  >
                    {m.name}
                  </a>
                  <a
                    href={urlDownloadModuloFirmato(data.id, m.id)}
                    className="flex items-center gap-1 text-xs text-brand hover:underline flex-shrink-0"
                  >
                    <Download size={14} /> Scarica
                  </a>
                  <select
                    value={m.tipo ?? ""}
                    onChange={(e) => handleSetTipo(m.id, e.target.value as ModuloTipo)}
                    aria-label={`Tipo modulo ${m.name}`}
                    className={`text-xs border rounded px-2 py-1 flex-shrink-0 ${m.tipo ? "border-border text-text" : "border-amber-400 bg-amber-50 text-amber-700"}`}
                  >
                    <option value="" disabled>— da assegnare —</option>
                    <option value="titolo_occupazione">Titolo occupazione</option>
                    <option value="regolarita_edilizia">Regolarità edilizia</option>
                  </select>
                  <button
                    onClick={() => setConfirmFirmato(m)}
                    className="p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                    aria-label={`Elimina ${m.name}`}
                    title="Elimina"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {data.moduli_stato && data.moduli_stato.richiesti.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Stato moduli richiesti
              </p>
              <ul className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
                {data.moduli_stato.richiesti.map((t) => {
                  const ok = data.moduli_stato!.presenti.includes(t);
                  return (
                    <li key={t} className={`flex items-center gap-1.5 ${ok ? "text-green-700" : "text-red-600"}`}>
                      {ok ? <Check size={16} className="flex-shrink-0" /> : <X size={16} className="flex-shrink-0" />}
                      {LABEL_MODULO[t]}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {data.description && (
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-2">Note</h3>
          <p className="text-sm text-text whitespace-pre-wrap">{data.description}</p>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Cancellare la pratica?"
        message="Sei sicuro? L'azione non è reversibile."
        confirmLabel="Cancella"
        busy={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteError(null); }}
      />

      <ConfirmDialog
        open={!!confirmFirmato}
        title="Eliminare il modulo firmato?"
        message={confirmFirmato ? `"${confirmFirmato.name}" verrà rimosso dalla pratica.` : ""}
        confirmLabel="Elimina"
        busy={deletingFirmato}
        onConfirm={async () => {
          if (!confirmFirmato) return;
          setDeletingFirmato(true);
          await handleDeleteFirmato(confirmFirmato.id);
          setDeletingFirmato(false);
          setConfirmFirmato(null);
        }}
        onCancel={() => setConfirmFirmato(null)}
      />
    </div>
  );
}
